import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AccessCodeData {
  tokenId: string;
  registrationId: string;
  eventId: string;
  eventTitle: string;
  category: string | null;
  subcategory: string | null;
  modality: string | null;
  player1Name: string | null;
  player2Name: string | null;
  playerPosition: 'player1' | 'player2';
  expiresAt: string;
}

interface UseClaimAccessCodeReturn {
  verifyCode: (code: string) => Promise<AccessCodeData | null>;
  claimCode: (tokenId: string) => Promise<boolean>;
  codeData: AccessCodeData | null;
  isVerifying: boolean;
  isClaiming: boolean;
  error: string | null;
  clearError: () => void;
}

export const useClaimAccessCode = (): UseClaimAccessCodeReturn => {
  const [codeData, setCodeData] = useState<AccessCodeData | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const clearError = () => setError(null);

  const verifyCode = async (code: string): Promise<AccessCodeData | null> => {
    if (!code || code.length !== 6) {
      setError('El código debe tener 6 caracteres');
      return null;
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Debes iniciar sesión para verificar el código');
        return null;
      }

      // Search for the token by access_code
      const { data: token, error: tokenError } = await supabase
        .from('participant_access_tokens')
        .select(`
          id,
          registration_id,
          expires_at,
          claimed_by,
          claimed_at,
          player_number,
          registrations (
            id,
            event_id,
            user_id,
            partner_user_id,
            category,
            subcategory,
            modality,
            player1_name,
            player2_name,
            events (
              id,
              title
            )
          )
        `)
        .eq('access_code', code.toUpperCase())
        .single();

      if (tokenError || !token) {
        console.error('Token verification error:', tokenError);
        setError('Código no encontrado o inválido');
        return null;
      }

      // Check if token is already claimed
      if (token.claimed_by) {
        if (token.claimed_by === user.id) {
          setError('Ya has reclamado este código anteriormente');
        } else {
          setError('Este código ya ha sido reclamado por otro usuario');
        }
        return null;
      }

      // Check if token is expired
      if (new Date(token.expires_at) < new Date()) {
        setError('Este código ha expirado');
        return null;
      }

      const registration = token.registrations as any;
      if (!registration) {
        setError('No se encontró la inscripción asociada');
        return null;
      }

      // Get player names directly from registration (set by organizer)
      let player1Name = registration.player1_name || 'Jugador 1';
      let player2Name: string | null = registration.player2_name || null;

      // If no manual names in registration, fallback to profiles
      if (!registration.player1_name && registration.user_id) {
        const { data: player1Profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', registration.user_id)
          .single();

        if (player1Profile?.full_name) {
          player1Name = player1Profile.full_name;
        }
      }

      if (!registration.player2_name && registration.partner_user_id) {
        const { data: player2Profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', registration.partner_user_id)
          .single();

        if (player2Profile?.full_name) {
          player2Name = player2Profile.full_name;
        }
      }

      // Determine which player position the current user should be
      // Use player_number from the token (1 = player1, 2 = player2)
      const playerPosition: 'player1' | 'player2' = 
        token.player_number === 1 ? 'player1' : 'player2';

      const eventData = registration.events as any;
      
      const accessCodeData: AccessCodeData = {
        tokenId: token.id,
        registrationId: registration.id,
        eventId: eventData?.id || registration.event_id,
        eventTitle: eventData?.title || 'Evento',
        category: registration.category,
        subcategory: registration.subcategory,
        modality: registration.modality,
        player1Name,
        player2Name,
        playerPosition,
        expiresAt: token.expires_at
      };

      setCodeData(accessCodeData);
      return accessCodeData;

    } catch (err) {
      console.error('Error verifying code:', err);
      setError('Error al verificar el código');
      return null;
    } finally {
      setIsVerifying(false);
    }
  };

  const claimCode = async (tokenId: string): Promise<boolean> => {
    setIsClaiming(true);
    setError(null);

    try {
      // Call edge function to claim the code (bypasses RLS)
      const { data, error: invokeError } = await supabase.functions.invoke('claim-access-code', {
        body: { tokenId }
      });

      if (invokeError) {
        console.error('Error invoking claim function:', invokeError);
        setError('Error al reclamar el código');
        return false;
      }

      if (data?.error) {
        console.error('Claim error from function:', data.error);
        setError(data.error);
        return false;
      }

      console.log('Claim successful:', data);

      toast({
        title: "¡Inscripción reclamada!",
        description: "Tu inscripción ha sido confirmada exitosamente.",
      });

      return true;

    } catch (err) {
      console.error('Error claiming code:', err);
      setError('Error al reclamar el código');
      return false;
    } finally {
      setIsClaiming(false);
    }
  };

  return {
    verifyCode,
    claimCode,
    codeData,
    isVerifying,
    isClaiming,
    error,
    clearError
  };
};
